import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown/with-html';
import { useTranslation } from '../../../../hooks/useTranslation';
import { CustomDialog } from '../common/custom-dialog';
import HelpOutlineIcon from '../icons/HelpIcon';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  makeStyles,
  Typography,
  Theme,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import colors from '../../../../themes/main-colors';

const useStyles = makeStyles((theme: Theme) => ({
  dialogContent: {
    padding: '1.5rem',
    scrollbarWidth: 'none',
    overflowY: 'auto',
    height: '600px',
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
    },
  },
  accordionRoot: {
    boxShadow: 'none',
    marginBottom: '1rem',
    '&:before': {
      display: 'none',
    },
    '&:last-child': {
      marginBottom: 0,
    },
  },
  accordionTitle: {
    fontWeight: 600,
    fontSize: '16px',
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      margin: '3px 0px',
    },
  },
  accordionDetails: {
    display: 'block',
  },
  answerStyles: {
    fontSize: '16px',
    margin: '15px',
  },
  helpIcon: {
    fontSize: 30,
    color: colors.white,
  },
}));

interface OutageStatusFAQProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

export const OutageStatusFAQ = ({ open, setOpen }: OutageStatusFAQProps) => {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const handleClose = () => {
    setOpen(false);
  };
  const outageFaqs = useMemo(
    () => [
      {
        question: t('OUTAGE_FAQ_Q1'),
        answer: t('OUTAGE_FAQ_A1'),
      },
      {
        question: t('OUTAGE_FAQ_Q2'),
        answer: t('OUTAGE_FAQ_A2'),
      },
      {
        question: t('OUTAGE_FAQ_Q3'),
        answer: t('OUTAGE_FAQ_A3'),
      },
      {
        question: t('OUTAGE_FAQ_Q4'),
        answer: t('OUTAGE_FAQ_A4'),
      },
      {
        question: t('OUTAGE_FAQ_Q5'),
        answer: richT('OUTAGE_FAQ_A5'),
      },
      {
        question: t('OUTAGE_FAQ_Q6'),
        answer: t('OUTAGE_FAQ_A6'),
      },
      {
        question: t('OUTAGE_FAQ_Q7'),
        answer: t('OUTAGE_FAQ_A7'),
      },
      {
        question: t('OUTAGE_FAQ_Q8'),
        answer: (
          <ReactMarkdown
            source={t('OUTAGE_FAQ_A8')}
            escapeHtml={false}
            linkTarget={'_blank'}
          />
        ),
      },
      {
        question: t('OUTAGE_FAQ_Q9'),
        answer: (
          <ReactMarkdown source={t('OUTAGE_FAQ_A9')} escapeHtml={false} />
        ),
      },
      {
        question: t('OUTAGE_FAQ_Q10'),
        answer: t('OUTAGE_FAQ_A10'),
      },
      {
        question: t('OUTAGE_FAQ_Q11'),
        answer: t('OUTAGE_FAQ_A11'),
      },
    ],
    [],
  );

  return (
    <CustomDialog
      open={open}
      onClose={handleClose}
      title={t('OUTAGE_FAQ')}
      titleIcon={<HelpOutlineIcon className={classes.helpIcon} />}
    >
      <Box className={classes.dialogContent}>
        {outageFaqs.map((faq, idx) => (
          <Accordion key={idx} className={classes.accordionRoot}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className={classes.accordionSummary}
            >
              <Typography>
                <p className={classes.accordionTitle}>{faq.question}</p>
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <Divider />
              <p className={classes.answerStyles}>{faq.answer}</p>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </CustomDialog>
  );
};
